import { useState } from "react";
import {
  useRunPrePumpBacktestMutation,
  useGetPrePumpBacktestRunsQuery,
  useGetPrePumpBacktestRunQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtTime } from "@/lib/format";
import { toast } from "sonner";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine,
} from "recharts";

/**
 * PrePumpBacktestCard — show the backtest results on the Pre-Pump page.
 *
 * Top section: latest run KPIs + "Run new backtest" form
 * Middle: cumulative P&L line chart from the latest run's trades
 * Bottom: recent runs table (params + outcomes)
 */
export function PrePumpBacktestCard() {
  const runs = useGetPrePumpBacktestRunsQuery(10);
  const latestRunId = runs.data?.[0]?.id;
  const latest = useGetPrePumpBacktestRunQuery(latestRunId as number, {
    skip: !latestRunId,
  });
  const [runNew, { isLoading: running }] = useRunPrePumpBacktestMutation();

  const [days, setDays] = useState("180");
  const [minScore, setMinScore] = useState("95");
  const [minVol, setMinVol] = useState("5");

  async function onRun() {
    try {
      const r: any = await runNew({
        days: Number(days),
        min_score: Number(minScore),
        min_vol_expansion: Number(minVol),
      }).unwrap();
      const s = r?.summary;
      toast.success(
        s ? `Done — ${s.trades} trades, ${s.win_rate_pct}% win, ${s.total_pnl_pct}% total`
          : `status: ${r?.status}`
      );
    } catch (e: any) {
      toast.error(`Backtest failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  // Build cumulative P&L curve from the latest run's trades
  let trades: any[] = [];
  try {
    if (latest.data?.results_json) {
      trades = JSON.parse(latest.data.results_json);
      trades.sort((a, b) => (a.entry_ts || "").localeCompare(b.entry_ts || ""));
    }
  } catch {}
  let cum = 0;
  const curve = trades.map((t) => {
    cum += t.pnl_pct || 0;
    return { ts: t.entry_ts, cum_pnl: Number(cum.toFixed(2)), pnl: t.pnl_pct, sym: t.symbol };
  });

  const latestRow = runs.data?.[0];

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-baseline gap-2 flex-wrap">
          <span>Backtest</span>
          <span className="text-xs text-muted font-normal">
            historical validation: if the bot had bought at score X back then, what would P&L be now?
          </span>
        </CardTitle>
        <div className="flex flex-wrap items-center gap-1">
          <input
            className="border rounded px-2 py-1 text-xs w-16 bg-bg text-fg"
            value={days} onChange={(e) => setDays(e.target.value)} title="days"
          />
          <span className="text-xs text-muted">d</span>
          <input
            className="border rounded px-2 py-1 text-xs w-16 bg-bg text-fg"
            value={minScore} onChange={(e) => setMinScore(e.target.value)} title="min score"
          />
          <span className="text-xs text-muted">score</span>
          <input
            className="border rounded px-2 py-1 text-xs w-16 bg-bg text-fg"
            value={minVol} onChange={(e) => setMinVol(e.target.value)} title="min vol expansion"
          />
          <span className="text-xs text-muted">vol×</span>
          <Button size="sm" onClick={onRun} disabled={running}>
            {running ? "Running…" : "Run backtest"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Latest run KPIs */}
        {latestRow ? (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
            <Kpi label="Trades" value={String(latestRow.trades)} />
            <Kpi label="Win rate"
                 value={`${latestRow.win_rate}%`}
                 tone={latestRow.win_rate >= 50 ? "good" : "bad"} />
            <Kpi label="Total P&L"
                 value={`${latestRow.total_pnl_pct >= 0 ? "+" : ""}${latestRow.total_pnl_pct.toFixed(1)}%`}
                 tone={latestRow.total_pnl_pct >= 0 ? "good" : "bad"} />
            <Kpi label="Avg / trade"
                 value={`${latestRow.avg_pnl_pct >= 0 ? "+" : ""}${latestRow.avg_pnl_pct.toFixed(2)}%`}
                 tone={latestRow.avg_pnl_pct >= 0 ? "good" : "bad"} />
            <Kpi label="Best"
                 value={`+${latestRow.best_pct.toFixed(1)}%`}
                 tone="good" />
            <Kpi label="Worst"
                 value={`${latestRow.worst_pct.toFixed(1)}%`}
                 tone="bad" />
          </div>
        ) : (
          <div className="text-sm text-muted">
            No backtest runs yet — adjust params above and hit "Run backtest"
          </div>
        )}

        {/* Cumulative P&L from the latest run's trades */}
        {curve.length > 1 && (
          <div>
            <div className="text-xs text-muted mb-1">
              cumulative simulated P&L (latest run, {curve.length} trades):
            </div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={curve}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                  <XAxis
                    dataKey="ts"
                    tickFormatter={(s) => s ? s.substring(5, 10) : ""}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis tick={{ fontSize: 10 }} unit="%" />
                  <Tooltip
                    contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 11 }}
                    formatter={(v: any, _name: any, p: any) => {
                      const t = p.payload;
                      return [`cum ${v}%  (${t.sym} ${t.pnl >= 0 ? "+" : ""}${t.pnl?.toFixed(2)}%)`, ""];
                    }}
                    labelFormatter={(s) => s}
                  />
                  <ReferenceLine y={0} stroke="#444" />
                  <Line type="monotone" dataKey="cum_pnl"
                        stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Recent runs */}
        {runs.data && runs.data.length > 1 && (
          <div>
            <div className="text-xs text-muted mb-1">recent backtest runs:</div>
            <Table>
              <THead>
                <TR>
                  <TH>When</TH>
                  <TH className="text-right">Trades</TH>
                  <TH className="text-right">Win%</TH>
                  <TH className="text-right">Total P&L</TH>
                  <TH className="text-right">Avg / trade</TH>
                  <TH>Params</TH>
                </TR>
              </THead>
              <TBody>
                {runs.data.slice(0, 10).map((r) => {
                  let p: any = {};
                  try { p = JSON.parse(r.params_json || "{}"); } catch {}
                  return (
                    <TR key={r.id}>
                      <TD className="text-xs whitespace-nowrap">{fmtTime(r.ts)}</TD>
                      <TD className="text-right">{r.trades}</TD>
                      <TD className="text-right">
                        <Badge tone={r.win_rate >= 50 ? "good" : "muted"}>
                          {r.win_rate}%
                        </Badge>
                      </TD>
                      <TD className={`text-right font-semibold tabular-nums ${
                        r.total_pnl_pct >= 0 ? "text-green-500" : "text-red-500"
                      }`}>
                        {r.total_pnl_pct >= 0 ? "+" : ""}{r.total_pnl_pct.toFixed(2)}%
                      </TD>
                      <TD className={`text-right tabular-nums ${
                        r.avg_pnl_pct >= 0 ? "text-green-500" : "text-red-500"
                      }`}>
                        {r.avg_pnl_pct >= 0 ? "+" : ""}{r.avg_pnl_pct.toFixed(2)}%
                      </TD>
                      <TD className="text-xs text-muted">
                        {p.days}d · sc{p.min_score} · vol{p.min_vol_expansion}× · trail+{(p.trail_activation_pct ?? 0) * 100}%/{(p.trail_distance_pct ?? 0) * 100}%
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
        )}

        {runs.isLoading && <Skeleton className="h-24 w-full" />}
      </CardContent>
    </Card>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: "good" | "bad" }) {
  const cls = tone === "good" ? "text-green-500"
            : tone === "bad" ? "text-red-500"
                              : "";
  return (
    <div className="border rounded p-2">
      <div className="text-xs text-muted">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
